import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useRender, useT } from '../context';

export interface CarouselBreakpoint {
  minWidth: number;
  items: number;
}

interface CarouselProps {
  className: string;
  items: ReactNode[];
  breakpoints: CarouselBreakpoint[];
  gap: number;
  autoplayMs?: number;
  speedMs?: number;
  label: string;
}

function itemsForWidth(width: number, breakpoints: CarouselBreakpoint[]) {
  let n = 1;
  for (const bp of [...breakpoints].sort((a, b) => a.minWidth - b.minWidth)) if (width >= bp.minWidth) n = bp.items;
  return n;
}

/**
 * Dependency-free replacement for the Owl Carousel instances of the theme. Renders the same DOM classes
 * (owl-carousel / owl-stage / owl-item / owl-nav) so the theme CSS applies. The number of visible items per
 * breakpoint is driven by CSS custom properties, so the server-rendered HTML already has the final layout.
 * Autoplay pauses on hover/focus and is disabled for users who prefer reduced motion.
 */
export function Carousel({ className, items, breakpoints, gap, autoplayMs = 5000, speedMs = 600, label }: CarouselProps) {
  const t = useT();
  const { mode } = useRender();
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(() => breakpoints.reduce((m, b) => Math.max(m, b.items), 1));
  const [paused, setPaused] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => setPerView(itemsForWidth(window.innerWidth, breakpoints));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [breakpoints]);

  const maxIndex = Math.max(0, items.length - perView);
  const safeIndex = Math.min(index, maxIndex);
  const go = useCallback((next: number) => setIndex(next < 0 ? maxIndex : next > maxIndex ? 0 : next), [maxIndex]);

  useEffect(() => {
    if (mode === 'editor' || paused || maxIndex === 0) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), autoplayMs);
    return () => window.clearInterval(timer);
  }, [paused, maxIndex, autoplayMs, mode]);

  const style = { '--kfz-gap': `${gap}px`, '--kfz-index': safeIndex, '--kfz-speed': `${speedMs}ms` } as CSSProperties;

  return (
    <div
      ref={rootRef}
      className={`${className} owl-carousel owl-theme owl-loaded kfz-carousel`}
      style={style}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') go(safeIndex - 1);
        if (e.key === 'ArrowRight') go(safeIndex + 1);
      }}
    >
      <div className="owl-stage-outer">
        <div className="owl-stage">
          {items.map((item, i) => {
            const visible = i >= safeIndex && i < safeIndex + perView;
            return (
              <div
                key={i}
                className={`owl-item${visible ? ' active' : ''}`}
                aria-roledescription="slide"
                aria-label={t('slideOf', { n: i + 1, total: items.length })}
                aria-hidden={visible ? undefined : true}
                // Hidden items must not be reachable by keyboard.
                {...(!visible ? { inert: '' } : {})}
              >
                {item}
              </div>
            );
          })}
        </div>
      </div>
      <div className="owl-controls">
      <div className={`owl-nav${maxIndex === 0 ? ' disabled' : ''}`}>
        <button type="button" className="owl-prev" aria-label={t('previous')} onClick={() => go(safeIndex - 1)}>
          <i className="fa fa-angle-left" aria-hidden="true" />
        </button>
        <button type="button" className="owl-next" aria-label={t('next')} onClick={() => go(safeIndex + 1)}>
          <i className="fa fa-angle-right" aria-hidden="true" />
        </button>
      </div>
      </div>
    </div>
  );
}
